import React, { useState } from "react";
import { useIntl } from "react-intl";

import PropTypes from "prop-types";
import cx from "classnames";

import Button from "@salesforce/design-system-react/components/button";

import Axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import FormElement from "@components/FormElement"; // eslint-disable-line

import { validateField } from "@services/validationService";
import SignatureIcon from "@assets/icons/Signature.svg";
import WebFormSuccessImg from "@assets/img/webform-success-img.svg";

// Assets and Styles
import "./styles/WebFormView.scss";

const axios = Axios.create();

function WebFormView({ form, isPreview, marketingDetails }) {
	const intl = useIntl();

	const [data, setData] = useState({});
	const [isSuccess, setIsSuccess] = useState(false);
	const [isShowErrorOnChange, setIsShowErrorOnChange] = useState(false);
	const [isAcceptedTerms, setAcceptedTerms] = useState(false);
	const [recaptchaToken, setRecaptchaToken] = useState(null);

	const [errorMessages, setErrorMessages] = useState({});

	const validate = () => {
		const errors = {};
		form.fields.map(each => {
			// todo: if TYP_TEXTAREA add validation of max character
			const res = validateField(
				{
					fieldId: each.fieldId,
					isRequired: each.required,
					typeCode: each.typeCode,
					value: data[each.fieldId] || "",
				},
				0,
				0,
			);
			if (res.message) errors[each.fieldId] = res.message;
		});
		setIsShowErrorOnChange(true);
		setErrorMessages(errors);
		if (form.submitOption.spamProtection && !recaptchaToken) return false;
		if (form.checkboxOptions.enableTerms && !isAcceptedTerms) return false;
		return !Object.keys(errors).length;
	};

	const validateOnChange = each => ev => {
		handleChange(each.fieldId, ev);
		if (!isShowErrorOnChange) return;
		const res = validateField(
			{
				fieldId: each.fieldId,
				isRequired: each.required,
				typeCode: each.typeCode,
				value: ev.value || "",
			},
			0,
			0,
		);

		setErrorMessages(({ ...errors }) => {
			errors[each.fieldId] = res.message || "";
			return errors;
		});
	};

	const handleChange = (fieldId, ev) => {
		if (isPreview) return;
		setData(({ ...value }) => {
			value[fieldId] = ev.value;
			return value;
		});
	};

	const getButtonPlacement = buttonPlacement => {
		if (buttonPlacement === "align-left") {
			return { width: "100%", textAlign: "left" };
		}
		if (buttonPlacement === "align-right") {
			return { width: "100%", textAlign: "right" };
		}
		if (buttonPlacement === "align-center") {
			return { width: "100%", textAlign: "center" };
		}
		return { with: "100%" };
	};

	const getButtonStyles = buttonPlacement => {
		if (
			buttonPlacement === "align-left" ||
			buttonPlacement === "align-right" ||
			buttonPlacement === "align-center"
		) {
			return { width: "fit-content", margin: "0 0 10px 0", padding: "0 40px" };
		}
		return { width: "100%", marginTop: 0 };
	};
	const redirect = () => {
		const redirectPath = form.submitOption.redirectUri.match("http")
			? form.submitOption.redirectUri
			: `https://${form.submitOption.redirectUri}`;
		if (window.self === window.top) {
			window.location.href = redirectPath;
		} else {
			window.top.location.href = redirectPath;
		}
	};

	const handleAcceptChange = ({ value }) => setAcceptedTerms(value);

	const handleSubmit = () => {
		if (isPreview) return;
		if (!validate()) return;
		axios
			.post(`${process.env.BACKEND_URI}/api/v2/web-form-response`, {
				form: form.uid,
				token: `${form.token}`,
				records: data,
				recaptchaToken,
				marketingDetails,
			})
			.then(() => {
				setIsSuccess(true);
				if (form.submitOption.onSuccess === "redirect") redirect();
				else if (form.submitOption.onSuccess === "success-message-redirect")
					setTimeout(redirect, 3000);
			})
			.catch(e => {
				// eslint-disable-next-line no-console
				console.error(e);
			});
	};

	return (
		<div className={`pc-webform-font-${form.style.fontStyle} pc-webform-font`}>
			<style>
				{`
					.pc-settings-create-webforms-preview {
						background: ${form.style.backgroundColor} !important;
					}
					.pc-webform-paper {
						background: #FFFFFF !important;
					}
					.pc-webform-btn {
						color: ${form.style.buttonTextColor} !important;
						background: ${form.style.primaryColor} !important;
						border-color: ${form.style.primaryColor} !important;
					}
					.field-lbl {
						color: ${form.style.textColor} !important;
					} 
					.pc-webform-title {
						color:  ${form.style.textColor} !important;
					}
					.pc-weform-checkbox {
						color:  ${form.style.textColor} !important;
					}
					.pc-weform-checkbox p {
						color:  ${form.style.textColor} !important;
					}
					`}
				{form.additionalStyle}
			</style>
			{/* eslint-disable-next-line no-constant-condition */}
			{(form.submitOption.onSuccess === "success-message" ||
				form.submitOption.onSuccess === "success-message-redirect") &&
			isSuccess ? (
				<div className="pc-webform-success-message">
					<div className="pc-webform-success-image pc-text-center">
						<img
							src={WebFormSuccessImg}
							alt="PC Webform Sucess"
							width="200px"
						/>
					</div>
					<br />
					<h3 className="pc-webform-success-title pc-text-center slds-m-vertical_large">
						{form.submitOption.successTitle}
					</h3>
					<br />
					{Boolean(form.submitOption.successBody) && (
						<p className="h3 pc-text-center">{form.submitOption.successBody}</p>
					)}
				</div>
			) : (
				<React.Fragment>
					<h3 className="pc-webform-title">{form.header.title}</h3>
					{Boolean(form.header.description) && (
						<p className="h3">{form.header.description}</p>
					)}
					<div className="pc-fields-container">
						{form.fields.map(each => (
							<div key={each.fieldId} className="pc-webform-field-wapper">
								{form.style.fieldPlacement === "label" ? (
									<p className="field-lbl">
										{each.label}{" "}
										{each.required && (
											<span className="pc-text-failure">*</span>
										)}
									</p>
								) : null}
								<FormElement
									// autoFocus={!isPreview}
									typeCode={each.typeCode}
									isClearable
									isRequired={each.required}
									placeholder={
										form.style.fieldPlacement === "placeholder"
											? each.label
											: ""
									}
									field={{
										fieldId: each.fieldId,
										params: each.params || [],
										mappedWith: each.mappedWith,
									}}
									className={cx(
										`pc-webform-field pc-webform-field-${form.style.fieldType} pc-webform-field-size-${form.style.fieldSize}`,
										errorMessages[each.fieldId] && "pc-error-form-element",
									)}
									options={each.params || []}
									handleChange={validateOnChange(each)}
									value={data[each.fieldId] || ""}
									disable={false}
									errorText={errorMessages[each.fieldId]}
									country={form.country}
									userInfo={form.userInfo}
								/>
							</div>
						))}
					</div>
					<div className="pc-webform-field-wapper">
						{form.checkboxOptions.enableTerms && (
							<React.Fragment>
								<div className="pc-weform-checkbox">
									<FormElement
										typeCode="TYP_CHECKBOX"
										isClearable
										handleChange={value => handleAcceptChange(value)}
										isRequired={form.checkboxOptions.enableTerms}
										value={isAcceptedTerms}
										disable={false}
									/>

									<article
										// eslint-disable-next-line react/no-danger
										dangerouslySetInnerHTML={{
											__html: form.checkboxOptions.privacyText,
										}}
									/>
								</div>

								<span
									className={`pc-error-message webformErrorMessage ${
										isShowErrorOnChange && !isAcceptedTerms ? "active" : ""
									}`}
								>
									{intl.formatMessage({
										id: "automation.aboveCheckbox",
									})}
								</span>
							</React.Fragment>
						)}
					</div>
					<div className="pc-webform-field-wapper pc-webform-captcha">
						{form.submitOption.spamProtection && (
							<React.Fragment>
								<ReCAPTCHA
									sitekey={process.env.RECAPTCHA_KEY}
									onChange={token => setRecaptchaToken(token)}
								/>

								<span
									className={`pc-error-message webformErrorMessage ${
										isShowErrorOnChange && !recaptchaToken ? "active" : ""
									}`}
								>
									{intl.formatMessage({
										id: "automation.aboveCheckbox",
									})}
								</span>
							</React.Fragment>
						)}
					</div>
					<div style={getButtonPlacement(form.submitOption.buttonPlacement)}>
						<Button
							id="SUBMIT"
							className={`slds-button slds-button--brand pc-webform-btn pc-webform-btn-${form.style.fieldType}`}
							onClick={handleSubmit}
							iconCategory="custom"
							style={getButtonStyles(form.submitOption.buttonPlacement)}
						>
							{form.submitOption.buttonLabel}
						</Button>
					</div>
					{!form.hideSignature && (
						<div className="pc-webform-powered-by">
							<a href="https://peppercloud.com/" target="_blank">
								<img src={SignatureIcon} alt="Powered By PepperCloud" />
							</a>
						</div>
					)}
				</React.Fragment>
			)}
		</div>
	);
}

WebFormView.propTypes = {
	form: PropTypes.object.isRequired,
	isPreview: PropTypes.bool,
	marketingDetails: PropTypes.object,
};

WebFormView.defaultProps = {
	isPreview: false,
};

export default WebFormView;
